import { useCallback, useEffect, useRef, useState } from 'react'
import { AudioFencePlayer } from '@/components/features/audio-fence-player'
import { loadAuthenticatedImageSrc } from '@/lib/authenticated-attachment-image'
import {
  audioSrcForFence,
  sha256Hex,
  stripLeadingAudioFence,
  type ParsedAudioFence,
} from '@/lib/audio-fence'
import { generateDocumentTts } from '@/lib/api/documents'
import {
  getSoftBookmark,
  resumeSecondsForAudio,
  setSoftBookmarkAudio,
} from '@/lib/document-soft-bookmark'

export function AudioFencePlayerLive({
  fence,
  documentId,
  lessonBody,
  onGenerated,
}: {
  fence: ParsedAudioFence
  documentId?: string | null
  lessonBody: string
  onGenerated?: () => void
}) {
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const [playableSrc, setPlayableSrc] = useState<string | null>(null)
  const [currentSeconds, setCurrentSeconds] = useState(0)
  const [durationSeconds, setDurationSeconds] = useState(0)
  const [isPlaying, setIsPlaying] = useState(false)
  const [isStale, setIsStale] = useState(false)
  const [isGenerating, setIsGenerating] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const remoteSrc = audioSrcForFence(fence)

  useEffect(() => {
    let cancelled = false
    if (!remoteSrc) {
      setPlayableSrc(null)
      return
    }
    void (async () => {
      try {
        const next = remoteSrc.startsWith('http') || remoteSrc.startsWith('blob:')
          ? remoteSrc
          : await loadAuthenticatedImageSrc(remoteSrc)
        if (!cancelled) setPlayableSrc(next)
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Could not load audio')
        }
      }
    })()
    return () => {
      cancelled = true
    }
  }, [remoteSrc])

  useEffect(() => {
    let cancelled = false
    void sha256Hex(stripLeadingAudioFence(lessonBody)).then((hash) => {
      if (cancelled) return
      setIsStale(Boolean(fence.sourceHash && hash !== fence.sourceHash))
    })
    return () => {
      cancelled = true
    }
  }, [fence.sourceHash, lessonBody])

  useEffect(() => {
    const node = audioRef.current
    if (!node || !playableSrc) return
    const onTime = () => setCurrentSeconds(node.currentTime || 0)
    const onMeta = () => {
      setDurationSeconds(node.duration && Number.isFinite(node.duration) ? node.duration : 0)
      const resume = resumeSecondsForAudio(
        getSoftBookmark(documentId),
        isStale ? null : fence.sourceHash,
        fence.attachmentId,
      )
      if (resume != null && resume > 0.5) {
        node.currentTime = resume
        setCurrentSeconds(resume)
      }
    }
    const onPlay = () => setIsPlaying(true)
    const onPause = () => {
      setIsPlaying(false)
      if (documentId && fence.attachmentId && fence.sourceHash && !isStale) {
        setSoftBookmarkAudio(documentId, {
          seconds: node.currentTime || 0,
          duration: node.duration && Number.isFinite(node.duration) ? node.duration : 0,
          attachmentId: fence.attachmentId,
          sourceHash: fence.sourceHash,
        })
      }
    }
    node.addEventListener('timeupdate', onTime)
    node.addEventListener('loadedmetadata', onMeta)
    node.addEventListener('play', onPlay)
    node.addEventListener('pause', onPause)
    return () => {
      node.removeEventListener('timeupdate', onTime)
      node.removeEventListener('loadedmetadata', onMeta)
      node.removeEventListener('play', onPlay)
      node.removeEventListener('pause', onPause)
    }
  }, [documentId, fence.attachmentId, fence.sourceHash, isStale, playableSrc])

  const onTogglePlay = useCallback(() => {
    const node = audioRef.current
    if (!node) return
    if (node.paused) void node.play()
    else node.pause()
  }, [])

  const onSeek = useCallback((seconds: number) => {
    const node = audioRef.current
    if (!node) return
    node.currentTime = seconds
    setCurrentSeconds(seconds)
  }, [])

  const onDownload = useCallback(() => {
    if (!playableSrc) return
    const a = document.createElement('a')
    a.href = playableSrc
    a.download = 'lesson.mp3'
    a.rel = 'noopener'
    a.click()
  }, [playableSrc])

  const onRegenerate = useCallback(async () => {
    if (!documentId || isGenerating) return
    setIsGenerating(true)
    setError(null)
    try {
      await generateDocumentTts(documentId)
      onGenerated?.()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not generate audio')
    } finally {
      setIsGenerating(false)
    }
  }, [documentId, isGenerating, onGenerated])

  return (
    <>
      {playableSrc ? (
        <audio ref={audioRef} src={playableSrc} preload="metadata" className="hidden" />
      ) : null}
      <AudioFencePlayer
        voiceId={fence.voiceId}
        currentSeconds={currentSeconds}
        durationSeconds={durationSeconds}
        isPlaying={isPlaying}
        isGenerating={isGenerating}
        isStale={isStale}
        error={error}
        onTogglePlay={onTogglePlay}
        onSeek={onSeek}
        onDownload={onDownload}
        onRegenerate={documentId ? () => void onRegenerate() : undefined}
      />
    </>
  )
}
